import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const ContactSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    rental_duration: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "contact",
          ...formData
        })
      });

      if (response.ok) {
        // Redirect to thank you page after successful submission
        navigate('/thank-you');
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to send inquiry. Please try again or call us directly.');
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '01255 823031',
      link: 'tel:01255823031',
    },
    {
      icon: MessageSquare,
      title: 'WhatsApp',
      value: '+44 7811 956789',
      link: 'https://wa.me/447811956789',
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'redsailselfstorage@gmail.com',
      link: 'mailto:redsailselfstorage@gmail.com',
    },
    {
      icon: MapPin,
      title: 'Address',
      value: 'Blackwater Farm, Lee Wick Lane, St. Osyth, Clacton-on-Sea, Essex, CO16 8ES',
      link: 'https://www.google.com/maps/place/Red+Sail+Self+Storage/@51.7906198,1.0657016,14.75z/data=!4m6!3m5!1s0x47d91305174b3999:0xd049b71ff0ddf7b3!8m2!3d51.7919515!4d1.0558218!16s%2Fg%2F11rqldndt7?hl=en-GB&entry=ttu&g_ep=EgoyMDI2MDUyMC4wIKXMDSoASAFQAw%3D%3D',
    },
    {
      icon: Clock,
      title: 'Business Hours',
      value: 'Daily 9:00 AM - 5:00 PM (Other times available on request)',
      link: null,
    },
  ];

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-red-50/20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-medium mb-4">
            <Send className="h-4 w-4 mr-2" />
            Get In Touch
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Contact Us Today
          </h2>
          <p className="text-lg text-gray-600">
            Ready to book your storage container? Have questions? We're here to help! Reach out via phone, 
            email, or fill out the form below.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="border-2 border-gray-200 shadow-xl">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6" name="contact">
                {/* Hidden input for Netlify Forms */}
                <input type="hidden" name="form-name" value="contact" />
                
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="w-full"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="07XXX XXXXXX"
                      className="w-full"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="rentalDuration" className="block text-sm font-medium text-gray-700 mb-2">
                    Desired Rental Duration
                  </label>
                  <select
                    id="rentalDuration"
                    name="rental_duration"
                    value={formData.rental_duration}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                  >
                    <option value="">Select duration...</option>
                    <option value="4-weeks">4 Weeks (Standard)</option>
                    <option value="12-weeks">12 Weeks (10% Discount)</option>
                    <option value="26-weeks">26 Weeks (15% Discount)</option>
                    <option value="52-weeks">52 Weeks (18.75% Discount)</option>
                    <option value="unsure">Not sure yet</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your storage needs..."
                    rows={5}
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold"
                >
                  {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                  {!isSubmitting && <Send className="ml-2 h-5 w-5" />}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  {contactInfo.map((info, index) => {
                    const Icon = info.icon;
                    return (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                          <Icon className="h-6 w-6 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">{info.title}</p>
                          {info.link ? (
                            <a
                              href={info.link}
                              target={info.icon === MapPin ? '_blank' : undefined}
                              rel={info.icon === MapPin ? 'noopener noreferrer' : undefined}
                              className="text-gray-600 hover:text-red-600 transition-colors"
                            >
                              {info.value}
                            </a>
                          ) : (
                            <p className="text-gray-600">{info.value}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Map */}
            <Card className="border-2 border-gray-200 overflow-hidden">
              <div className="h-64 bg-gray-200">
                <iframe
                  title="Red Sail Self Storage Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2486.1234!2d1.0558218!3d51.7919515!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d91305174b3999%3A0xd049b71ff0ddf7b3!2sRed%20Sail%20Self%20Storage!5e0!3m2!1sen!2suk!4v1716000000000!5m2!1sen!2suk"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </Card>

            {/* Quick Action Card */}
            <Card className="border-2 border-red-200 bg-red-50">
              <CardContent className="p-6">
                <h4 className="font-semibold text-gray-900 mb-2">Need Immediate Assistance?</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Call us directly or send a WhatsApp message for quick responses.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href="tel:01255823031">
                    <Button className="bg-red-600 hover:bg-red-700 text-white">
                      <Phone className="h-4 w-4 mr-2" />
                      Call Now
                    </Button>
                  </a>
                  <a href="https://wa.me/447811956789" target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="border-2 border-red-600 text-red-600 hover:bg-red-50">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
